import React from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  useTheme
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  Legend
} from 'recharts';
import { TeacherDashboardData } from '@/types/models';
import { dashboardStyles } from '@/layouts/TeacherLayout';

interface AnalyticsTabProps {
  data: TeacherDashboardData;
}

const AnalyticsTab: React.FC<AnalyticsTabProps> = ({ data }) => {
  const theme = useTheme();

  // Prepare skills data
  const skillsData = Object.entries(data.skills_analytics.skill_distribution)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 10);

  // Prepare interests data
  const interestsData = Object.entries(data.interests_analytics.interest_distribution)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 10);

  // Prepare engagement metrics for radar chart
  const maxStudents = data.class_stats.total_students;
  const engagementData = [
    {
      metric: 'Active Students',
      value: (data.class_stats.active_students / maxStudents) * 100,
      fullMark: 100
    },
    {
      metric: 'With Skills',
      value: ((maxStudents - data.skills_analytics.students_without_skills) / maxStudents) * 100,
      fullMark: 100
    },
    {
      metric: 'With Interests',
      value: ((maxStudents - data.interests_analytics.students_without_interests) / maxStudents) * 100,
      fullMark: 100
    },
    {
      metric: 'Applied',
      value: (data.class_stats.applications.total / maxStudents) * 100,
      fullMark: 100
    },
    {
      metric: 'Logged Hours',
      value: (data.class_stats.volunteer_logs.total / maxStudents) * 100,
      fullMark: 100
    }
  ];

  const COLORS = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    ...Array(5).fill(theme.palette.grey[500])
  ];

  return (
    <Box>
      {/* Top Level Metrics */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper sx={dashboardStyles.dashboard.analytics.card}>
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Skills Coverage
              </Typography>
              <Typography variant="h3" fontWeight="bold" color="primary">
                {data.skills_analytics.total_unique_skills}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Unique skills across all students
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={dashboardStyles.dashboard.analytics.card}>
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Interest Areas
              </Typography>
              <Typography variant="h3" fontWeight="bold" color="secondary">
                {data.interests_analytics.total_unique_interests}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Distinct areas of interest
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={dashboardStyles.dashboard.analytics.card}>
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Engagement Score
              </Typography>
              <Typography variant="h3" fontWeight="bold" color="success.main">
                {Math.round((data.class_stats.active_students / data.class_stats.total_students) * 100)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Of students actively participating
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mt: 3 }}>
        {/* Skills Distribution */}
        <Grid item xs={12} md={6}>
          <Paper sx={dashboardStyles.dashboard.analytics.chart}>
            <Typography variant="h6" gutterBottom>
              Top Skills Distribution
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={skillsData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={150} />
                <Tooltip />
                <Bar dataKey="value" fill={theme.palette.primary.main} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Interests Distribution */}
        <Grid item xs={12} md={6}>
          <Paper sx={dashboardStyles.dashboard.analytics.chart}>
            <Typography variant="h6" gutterBottom>
              Interest Areas Distribution
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <Pie
                  data={interestsData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={120}
                  fill="#8884d8"
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {interestsData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Student Engagement Radar */}
        <Grid item xs={12}>
          <Paper sx={dashboardStyles.dashboard.analytics.chart}>
            <Typography variant="h6" gutterBottom>
              Class Engagement Metrics
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <RadarChart cx="50%" cy="50%" outerRadius="80%" data={engagementData}>
                <PolarGrid gridType="polygon" />
                <PolarAngleAxis 
                  dataKey="metric" 
                  tick={{ fill: theme.palette.text.primary }}
                  axisLine={{ stroke: theme.palette.divider }}
                />
                <Radar
                  name="Class Engagement"
                  dataKey="value"
                  stroke={theme.palette.primary.main}
                  fill={theme.palette.primary.main}
                  fillOpacity={0.6}
                />
                <Legend />
              </RadarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AnalyticsTab; 